import React, { useMemo, useRef, useState } from 'react';
import { Card, Dialog, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ImageGalleryLib from 'react-image-gallery';
import { IProduct } from '../../../../interfaces/product';
import { API_DOMAIN } from '../../../../constants';
import { toAbsoluteUrl } from '../../../../../_base';
import { useFormatMessage } from '../../../../hooks';

interface IProps {
  product: IProduct;
}

const useStyles = makeStyles((theme: any) => ({
  imgContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 0,
    backgroundColor: 'transparent',
    height: '100%',
    position: 'relative',
    width: 140,
  },
  imgContainerSmall: {
    maxHeight: '250px',
    borderRadius: 0,
    [theme.breakpoints.up('md')]: {
      maxHeight: '85vh',
      borderRadius: 0,
    },
  },
  imgContainerFull: {
    height: '100vh',
    width: '100%',
    borderRadius: 0,
    '& img, .image-gallery-slides': {
      height: '100% !important',
      width: '100% !important',
    },
  },
  img: {
    width: 'auto',
    maxWidth: '100%',
    minWidth: 400,
    height: 'auto',
    maxHeight: '100%',
    objectFit: 'contain',
    objectPosition: 'center center',
    backgroundColor: 'transparent',
    [theme.breakpoints.down(1340)]: {
      minWidth: 300,
    },
  },
  galleryContainer: {
    width: '100%',
    maxWidth: '400px',
    [theme.breakpoints.down(1240)]: {
      maxWidth: '100%',
    },
  },
  imageDescription: {
    fontSize: 16,
    color: '#181c3266',
    fontWeight: theme.typography.fontWeightRegular,
  },
  customImageGallery: {
    maxWidth: '400px',
    [theme.breakpoints.down(1240)]: {
      maxWidth: '100%',
    },
    // maxHeight: '400px',
    // [theme.breakpoints.up(600)]: {
    //   '& .image-gallery-slide-wrapper.image-gallery-thumbnails-right': {
    //     width: 450,
    //     height: 450,
    //   },
    // },

    '& .image-gallery-left-nav': {
      left: 15,
    },

    '& .image-gallery-icon, .image-gallery-right-nav': {
      filter: 'none !important',
    },

    '& .image-gallery-left-nav .image-gallery-svg, .image-gallery-right-nav .image-gallery-svg':
      {
        height: 32,
        width: 32,
        borderRadius: '50%',
        backgroundColor: '#a3a7b880',
        filter: 'none !important',
      },

    '& .image-gallery-slide-wrapper  .image-gallery-thumbnails-left': {
      width: '100% !important',
    },

    '& .image-gallery-slides, .image-gallery-slides img': {
      height: '400px',
      // objectFit: 'cover !important',
      borderRadius: '24px !important',
      backgroundColor: '#fff',
      // width: '100%',
    },
    '& .image-gallery-fullscreen-button': {
      backgroundColor: '#a3a7b880 !important',
      position: 'absolute !important',
      right: '10px !important',
      bottom: '10px !important',
      '& span': {
        width: '0 !important',
        height: '0 !important',
      },
    },
    '& .image-gallery-thumbnails-container': {
      top: -9,
      // height: 390,
      display: 'flex',
      // flexDirection: 'column',
      gap: 30,
    },
    '& .image-gallery-swipe': {
      // marginLeft: 15,
    },

    '& .image-gallery-content': { display: 'flex', gap: 15, flexDirection: 'column' },
    '& .image-gallery-thumbnails': { height: '135px !important' },
    '& .image-gallery-thumbnail-inner img,  .image-gallery-thumbnails-wrapper.image-gallery-thumbnails-right, span':
      {
        width: '125px !important',
        height: '125px !important',
        borderRadius: '24px !important',
      },
  },
  moreImageContainer: {
    '& .image-gallery-slide-wrapper.image-gallery-thumbnails-left, .image-gallery-slide-wrapper.image-gallery-thumbnails-right':
      {
        width: '100%',
      },
  },
  '& .image-gallery-thumbnails-wrapper .image-gallery-thumbnails-left .thumbnails-swipe-vertical':
    {
      width: '135px !important',
    },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    color: '#205373',
  },
  closeBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 10,
  },
  fullscreenImg: {
    maxWidth: '90vw',
    maxHeight: '90vh',
    objectFit: 'contain',
  },
  dialogContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 0,
    position: 'relative',
  },
}));

const ImageGallery: React.FC<IProps> = ({ product }) => {
  const classes = useStyles();
  const galleryRef = useRef<ImageGalleryLib | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const fm = useFormatMessage();

  const handleImageClick = (imgUrl: string) => {
    setCurrentImage(imgUrl);
    setIsFullScreen(true);
  };

  const handleClose = async () => {
    await setIsFullScreen(false);
    setCurrentImage(null);
  };

  const images = useMemo(() => {
    if (product?.photos) {
      return product.photos
        .sort(a => (a.main ? -1 : 0))
        .map(el => ({
          original: el.big ? API_DOMAIN + el.big : toAbsoluteUrl('/images/placeholder.png'),
          thumbnail: el.small
            ? API_DOMAIN + el.small
            : toAbsoluteUrl('/images/placeholder.png'),
          renderItem: ({ original }: any) => (
            <>
              <img
                alt=''
                className={classes.img}
                src={original}
                onClick={() => handleImageClick(original)}
              />
            </>
          ),
        }));
    }

    return null;
  }, [product, isFullScreen]);

  return (
    <div className={classes.galleryContainer}>
      {images && (
        <>
          {images.length > 0 ? (
            <ImageGalleryLib
              ref={galleryRef}
              onScreenChange={boolean => setIsFullScreen(boolean)}
              items={images}
              useBrowserFullscreen={false}
              additionalClass={`${classes.customImageGallery}
              } ${images.length <= 1 && classes.moreImageContainer}`}
              showThumbnails={images.length > 1}
              showFullscreenButton
              renderFullscreenButton={() => (
                <IconButton
                  onClick={() => {
                    const currentIndex = galleryRef.current?.getCurrentIndex?.() || 0;
                    const current = images?.[currentIndex];
                    if (current?.original) {
                      handleImageClick(current.original);
                    }
                  }}
                  className='image-gallery-icon image-gallery-fullscreen-button'
                >
                  <img src={toAbsoluteUrl('/media/icons/full-screen.svg')} alt='Full screen' />
                </IconButton>
              )}
              disableThumbnailScroll={false}
              showPlayButton={false}
              thumbnailPosition='bottom'
              infinite
              onThumbnailError={(e: any) => {
                e.target.src = toAbsoluteUrl('/images/placeholder.png');
              }}
            />
          ) : (
            <Card
              className={`${classes.imgContainer} ${classes.imgContainerSmall}`}
              elevation={0}
            >
              <img
                alt=''
                className={classes.img}
                src={toAbsoluteUrl('/images/placeholder.png')}
              />
            </Card>
          )}
          <Typography
            component='p'
            className={classes.imageDescription}
            style={{ marginTop: 10 }}
          >
            {fm('IMAGE.DESCRIPTION')}
          </Typography>
          <Dialog
            open={isFullScreen}
            onClose={handleClose}
            maxWidth='xl'
            PaperProps={{
              style: { background: 'transparent', boxShadow: 'none', position: 'relative' },
            }}
          >
            <div className={classes.dialogContent}>
              <img
                src={currentImage || ''}
                alt='Fullscreen'
                className={classes.fullscreenImg}
              />
            </div>
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                borderRadius: 8,
                width: 40,
                height: 40,
              }}
              color='error'
            >
              ✕
            </IconButton>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default ImageGallery;
